// ResumoRGPapel.tsx
// Fechamento de Área, aba "2 · Fechamento": seção "Resumo do Reconhecimento Geográfico (FA-D-06)"
// no modo "passar para o papel". Desenho aprovado pelo Walber no Claude Design em 01/10/2026.
// SÓ LEITURA: não grava nada no servidor. Linhas copiadas, linha atual e ajustes de subtotal ficam
// guardados só neste navegador, por área + ciclo (chave "tovis-fad06-papel-<área>-<ciclo>").
// Regras do papel: cada folha tem 2 tabelas (esquerda e direita), cada uma com 22 LINHAS. Da 2ª tabela
// em diante, a 1ª linha é o Transporte, que GASTA uma das 22: sobram 21 lançamentos (regra do Walber,
// 02/10/2026). A 1ª tabela não tem Transporte: 22 lançamentos. O Walber pode subir ou descer o subtotal
// de cada tabela e tudo o que vem depois é recalculado. O rodapé é "Subtotal" (esquerda) ou "TOTAL"
// (direita) e é sempre ACUMULADO desde o 1º quarteirão. Zero aparece como "-", como no papel.
// DESAPARECIDOS (02/10/2026, regra do Walber): falha na numeração dos quarteirões (ex.: 9 e depois 18)
// vira uma linha "desaparecido" para cada número que falta (10 a 17). No papel, quarteirão com imóveis e
// desaparecido são escritos linha por linha, do mesmo jeito: o desaparecido CONTA nas 22 linhas do
// subtotal, mas NÃO entra em nenhum total. Calculado na tela: nada é gravado no RG nem no banco.
// Agente, período e situação aparecem só na lupa (não vão para o papel).
// FORA DE USO (20/10/2026, regra do Walber): se o número da falha tem registro "fora de uso" no RG (ex.: 16
// "QUARTEIRÃO ENGLOBADO NO Q15"), a MESMA linha mostra o motivo no lugar de "Desaparecido" (fora de todo total).
// Fora de uso que não cai em falha (ex.: 18.1 com o 18 no RG) não ganha linha: só aparece na faixa de conferência.
import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface LinhaRGPapel {
  codigo: string;
  total: number;
  R: number;
  C: number;
  TB: number;
  PE: number;
  O: number;
  agentes?: { nome: string; lancamentos: number }[];
  agente_principal?: string;
  data_inicio?: string;
  data_fim_fad?: string;
  data_toggle?: string;
}

export interface ForaDeUsoRG {
  codigo: string;
  motivo: string | null;
}

export interface FlagRGPapel {
  codigo: string;
  flag: string;
}

interface Props {
  quarteiroes: LinhaRGPapel[];
  conciliacao: FlagRGPapel[];
  totalImoveisRG: number;
  totalQuarteiroesRG: number;
  areaId: number | string | null | undefined;
  cicloId: number | string | null | undefined;
  // dados.rg.fora_de_uso; se não vier, a tela busca no RG (GET /api/rg/areas/{id}/quarteiroes)
  foraDeUso?: ForaDeUsoRG[];
}

type Tipo = 'R' | 'C' | 'TB' | 'PE' | 'O';
const TIPOS: Tipo[] = ['R', 'C', 'TB', 'PE', 'O'];
const NOMES_TIPO: Record<Tipo, string> = {
  R: 'Residencial',
  C: 'Comercial',
  TB: 'Terreno baldio',
  PE: 'Ponto estrat.',
  O: 'Outro',
};
const NOMES_CURTOS: Record<Tipo, string> = {
  R: 'Resid.',
  C: 'Comerc.',
  TB: 'Terreno baldio',
  PE: 'Ponto estrat.',
  O: 'Outro',
};
const PADRAO = 22;
const LINHAS_DO_PAPEL = 22;
const LIMITE_UM_POR_LINHA = 60;
const VERSAO_SALVO = 3;

// Lançamentos por tabela: 22 na primeira; 21 nas seguintes, porque o Transporte gasta a 1ª linha do papel
const padraoDaTabela = (k: number): number => (k === 0 ? PADRAO : PADRAO - 1);

interface Valores {
  R: number;
  C: number;
  TB: number;
  PE: number;
  O: number;
  total: number;
}

interface Linha {
  id: string;
  tipo: 'qrt' | 'transporte' | 'rodape' | 'desap';
  rotulo: string;
  vals: Valores;
  tab: number;
  linhaPapel?: number;
  q?: LinhaRGPapel;
  flag?: string;
  ultima?: boolean;
  qtd?: number;
  motivo?: string;
}

interface Tabela {
  k: number;
  linhas: Linha[];
  tam: number;
  reais: number;
  desap: number;
  padrao: number;
  ajustada: boolean;
  primeiro: string;
  ultimoCod: string;
  ultima: boolean;
}

interface Falha {
  de: number;
  ate: number;
  qtd: number;
  antesDe: number;
}

interface Salvo {
  copiadas: Record<string, boolean>;
  cursor: string | null;
  tamanhos: Record<string, number>;
}

interface Celula {
  rot: string;
  v: string;
  destaque: 'primeira' | 'normal' | 'total' | 'aviso';
  zero?: boolean;
  palavra?: boolean;
  larga?: boolean;
}

const COR = {
  navy: '#0F2347',
  azul: '#1D4ED8',
  texto: '#0F172A',
  apagado: '#64748B',
  secundario: '#475569',
  borda: '#CBD5E1',
  teal: '#0E7490',
  verdeFundo: '#DCFCE7',
  verdeTexto: '#166534',
  vermelho: '#DC2626',
  amarelo: '#CA8A04',
};

const ZERO: Valores = { R: 0, C: 0, TB: 0, PE: 0, O: 0, total: 0 };

const GRADE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '24px 92px repeat(5, minmax(0, 1fr)) 64px',
  alignItems: 'center',
};

const KBD: React.CSSProperties = {
  display: 'inline-flex',
  minWidth: 24,
  justifyContent: 'center',
  padding: '2px 7px',
  border: '1px solid #CBD5E1',
  borderBottomWidth: 2,
  borderRadius: 6,
  background: '#F8FAFC',
  fontFamily: 'monospace',
  fontSize: 12,
  color: '#0F172A',
};

const fmt = (n: number): string => (n === 0 ? '-' : n.toLocaleString('pt-BR'));

const plural = (n: number, um: string, varios: string): string => (n === 1 ? um : varios);

const fundoCelula = (d: Celula['destaque']): string =>
  d === 'total' ? COR.navy : d === 'primeira' ? '#EEF4FF' : d === 'aviso' ? '#F1F5F9' : '#F8FAFC';

const bordaCelula = (d: Celula['destaque']): string =>
  d === 'total'
    ? `1px solid ${COR.navy}`
    : d === 'primeira'
    ? '1px solid #93B4F5'
    : d === 'aviso'
    ? '1px dashed #94A3B8'
    : `1px solid ${COR.borda}`;

const corRotuloCelula = (d: Celula['destaque']): string =>
  d === 'total' ? '#A9BCE0' : d === 'primeira' ? COR.azul : COR.secundario;

const corValorCelula = (c: Celula): string =>
  c.destaque === 'total'
    ? '#FFFFFF'
    : c.destaque === 'primeira'
    ? '#1E3A8A'
    : c.destaque === 'aviso'
    ? '#334155'
    : c.zero
    ? COR.apagado
    : COR.texto;

function lerSalvo(chave: string): Salvo {
  const vazio: Salvo = { copiadas: {}, cursor: null, tamanhos: {} };
  try {
    const texto = window.localStorage.getItem(chave);
    if (!texto) return vazio;
    const o = JSON.parse(texto);
    // Progresso guardado com uma regra de subtotal mais antiga: os subtotais mudaram de lugar e de valor,
    // então as marcas de Transporte/Subtotal/TOTAL e os ajustes antigos não valem mais.
    const atual = o.v === VERSAO_SALVO;
    const copiadas: Record<string, boolean> = {};
    if (Array.isArray(o.copiadas)) {
      o.copiadas.forEach((id: unknown) => {
        if (typeof id !== 'string') return;
        const tp = id.charAt(0);
        if (!atual && (tp === 's' || tp === 't')) return;
        copiadas[id] = true;
      });
    }
    const tamanhos: Record<string, number> = {};
    if (atual && o.tamanhos && typeof o.tamanhos === 'object') {
      Object.keys(o.tamanhos).forEach((k) => {
        const v = o.tamanhos[k];
        if (typeof v === 'number' && v >= 1) tamanhos[k] = Math.round(v);
      });
    }
    return { copiadas, cursor: typeof o.cursor === 'string' ? o.cursor : null, tamanhos };
  } catch {
    return vazio;
  }
}

// Número inteiro do começo do código do quarteirão ("18" -> 18, "18.1" -> 18). Sem número -> null.
function numeroDe(codigo: string): number | null {
  const m = /^\s*(\d+)/.exec(codigo || '');
  return m ? parseInt(m[1], 10) : null;
}

// Falhas na numeração, só ENTRE o menor e o maior quarteirão da área. Subdivisão (18.1) não é falha.
function acharFalhas(qs: LinhaRGPapel[]): Falha[] {
  const primeiroIdx = new Map<number, number>();
  qs.forEach((q, i) => {
    const n = numeroDe(q.codigo);
    if (n !== null && !primeiroIdx.has(n)) primeiroIdx.set(n, i);
  });
  const nums = Array.from(primeiroIdx.keys()).sort((a, b) => a - b);
  const falhas: Falha[] = [];
  for (let j = 1; j < nums.length; j++) {
    const a = nums[j - 1];
    const b = nums[j];
    if (b > a + 1) {
      falhas.push({ de: a + 1, ate: b - 1, qtd: b - a - 1, antesDe: primeiroIdx.get(b) as number });
    }
  }
  return falhas;
}

// Uma linha para cada quarteirão desaparecido. Só uma falha absurda (mais de 60 números, sinal de erro
// de digitação no RG) vira uma linha única com a faixa, para não gerar centenas de linhas.
// Fora de uso: o número com registro no RG troca "Desaparecido" pelo motivo (mesma linha, mesmo id).
function linhasDaFalha(f: Falha, foraPorNumero: Map<number, ForaDeUsoRG>): Linha[] {
  const saida: Linha[] = [];
  if (f.qtd <= LIMITE_UM_POR_LINHA) {
    for (let x = f.de; x <= f.ate; x++) {
      const fu = foraPorNumero.get(x);
      saida.push({ id: `d${x}`, tipo: 'desap', rotulo: String(x), vals: { ...ZERO }, tab: 0, qtd: 1,
        ...(fu ? { motivo: fu.motivo || 'FORA DE USO' } : {}) });
    }
  } else {
    saida.push({ id: `d${f.de}-${f.ate}`, tipo: 'desap', rotulo: `${f.de} a ${f.ate}`, vals: { ...ZERO }, tab: 0, qtd: f.qtd });
  }
  return saida;
}

function montar(
  qs: LinhaRGPapel[],
  flags: Map<string, string>,
  tamanhos: Record<string, number>,
  falhas: Falha[],
  foraPorNumero: Map<number, ForaDeUsoRG> = new Map()
): Tabela[] {
  const antes: Record<number, Falha[]> = {};
  for (let f = 0; f < falhas.length; f++) {
    const alvo = falhas[f].antesDe;
    if (!antes[alvo]) antes[alvo] = [];
    antes[alvo].push(falhas[f]);
  }
  // 1) Uma fila única de lançamentos, na ordem do papel: cada desaparecido entra no lugar da falha
  const fila: Linha[] = [];
  const vistos: Record<string, number> = {};
  for (let g = 0; g < qs.length; g++) {
    const lista = antes[g] || [];
    for (let f = 0; f < lista.length; f++) {
      const novas = linhasDaFalha(lista[f], foraPorNumero);
      for (let n = 0; n < novas.length; n++) fila.push(novas[n]);
    }
    const q = qs[g];
    const vez = (vistos[q.codigo] || 0) + 1;
    vistos[q.codigo] = vez;
    fila.push({
      id: vez === 1 ? `q${q.codigo}` : `q${q.codigo}#${vez}`,
      tipo: 'qrt',
      rotulo: q.codigo,
      vals: { R: q.R || 0, C: q.C || 0, TB: q.TB || 0, PE: q.PE || 0, O: q.O || 0, total: q.total || 0 },
      tab: 0,
      q,
      flag: flags.get(q.codigo) || 'ok',
    });
  }
  // 2) Corta a fila em tabelas: 22 lançamentos na 1ª e 21 nas seguintes. Só quem tem imóveis soma.
  const tabs: Tabela[] = [];
  const acc: Valores = { ...ZERO };
  let i = 0;
  let k = 0;
  while (i < fila.length && k < 400) {
    const pedido = tamanhos[String(k)];
    const padrao = padraoDaTabela(k);
    const tam = Math.max(1, typeof pedido === 'number' ? pedido : padrao);
    const fim = Math.min(fila.length, i + tam);
    const linhas: Linha[] = [];
    // Da 2ª tabela em diante o Transporte é a linha 1 do papel, e os lançamentos começam na linha 2
    const temTransporte = k > 0;
    if (temTransporte) {
      linhas.push({ id: `t${k}`, tipo: 'transporte', rotulo: 'Transporte', vals: { ...acc }, tab: k, linhaPapel: 1 });
    }
    let reais = 0;
    let desap = 0;
    for (let j = i; j < fim; j++) {
      const l = fila[j];
      l.tab = k;
      l.linhaPapel = j - i + 1 + (temTransporte ? 1 : 0);
      if (l.tipo === 'qrt') {
        acc.R += l.vals.R;
        acc.C += l.vals.C;
        acc.TB += l.vals.TB;
        acc.PE += l.vals.PE;
        acc.O += l.vals.O;
        acc.total += l.vals.total;
        reais += 1;
      } else {
        desap += l.qtd || 1;
      }
      linhas.push(l);
    }
    const ultima = fim >= fila.length;
    linhas.push({ id: `s${k}`, tipo: 'rodape', rotulo: k % 2 === 0 ? 'Subtotal' : 'TOTAL', vals: { ...acc }, tab: k, ultima });
    tabs.push({
      k,
      linhas,
      tam: fim - i,
      reais,
      desap,
      padrao,
      ajustada: typeof pedido === 'number' && pedido !== padrao,
      primeiro: fila[i].rotulo,
      ultimoCod: fila[fim - 1].rotulo,
      ultima,
    });
    i = fim;
    k += 1;
  }
  return tabs;
}

export default function ResumoRGPapel(props: Props) {
  const { conciliacao, totalImoveisRG, totalQuarteiroesRG } = props;
  const qs = useMemo(() => props.quarteiroes || [], [props.quarteiroes]);
  const chave = `tovis-fad06-papel-${String(props.areaId ?? '')}-${String(props.cicloId ?? '')}`;

  const [copiadas, setCopiadas] = useState<Record<string, boolean>>({});
  const [cursor, setCursor] = useState<string | null>(null);
  const [tamanhos, setTamanhos] = useState<Record<string, number>>({});
  const [soProblemas, setSoProblemas] = useState(false);
  const [carregado, setCarregado] = useState<string | null>(null);
  const rolar = useRef(false);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  // Carrega o progresso guardado desta área + ciclo (só neste navegador)
  useEffect(() => {
    const s = lerSalvo(chave);
    setCopiadas(s.copiadas);
    setCursor(s.cursor);
    setTamanhos(s.tamanhos);
    setSoProblemas(false);
    setCarregado(chave);
  }, [chave]);

  // Guarda o progresso a cada mudança
  useEffect(() => {
    if (carregado !== chave) return;
    try {
      window.localStorage.setItem(
        chave,
        JSON.stringify({
          v: VERSAO_SALVO,
          copiadas: Object.keys(copiadas).filter((id) => copiadas[id]),
          cursor,
          tamanhos,
        })
      );
    } catch {
      /* navegador sem armazenamento: segue sem guardar */
    }
  }, [chave, carregado, copiadas, cursor, tamanhos]);

  const flags = useMemo(() => {
    const m = new Map<string, string>();
    (conciliacao || []).forEach((c) => m.set(c.codigo, c.flag));
    return m;
  }, [conciliacao]);

  // Fora de uso: vem por prop; sem prop, busca no RG da área (a lista traz as linhas FORA_DE_USO)
  const [foraBuscado, setForaBuscado] = useState<ForaDeUsoRG[]>([]);
  useEffect(() => {
    if (props.foraDeUso !== undefined || props.areaId === null || props.areaId === undefined || props.areaId === '') return;
    let vivo = true;
    fetch(`/api/rg/areas/${props.areaId}/quarteiroes`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d: any) => {
        if (!vivo || !Array.isArray(d)) return;
        setForaBuscado(d.filter((q: any) => q && q.status === 'FORA_DE_USO')
          .map((q: any) => ({ codigo: String(q.codigo), motivo: q.motivo ?? null })));
      })
      .catch(() => { /* sem a lista: segue como "desaparecido" */ });
    return () => { vivo = false; };
  }, [props.foraDeUso, props.areaId]);
  const foraDeUso = useMemo(() => props.foraDeUso ?? foraBuscado, [props.foraDeUso, foraBuscado]);

  const falhas = useMemo(() => acharFalhas(qs), [qs]);
  // Só ganha linha o fora de uso cuja parte inteira cai numa falha de até 60 números (linha por número)
  const { foraPorNumero, foraSemLinha } = useMemo(() => {
    const noBuraco = new Set<number>();
    falhas.forEach((f) => {
      if (f.qtd <= LIMITE_UM_POR_LINHA) for (let x = f.de; x <= f.ate; x++) noBuraco.add(x);
    });
    const m = new Map<number, ForaDeUsoRG>();
    const sem: ForaDeUsoRG[] = [];
    foraDeUso.forEach((fu) => {
      const n = numeroDe(fu.codigo);
      if (n !== null && noBuraco.has(n) && !m.has(n)) m.set(n, fu);
      else sem.push(fu);
    });
    return { foraPorNumero: m, foraSemLinha: sem };
  }, [foraDeUso, falhas]);
  const tabs = useMemo(() => montar(qs, flags, tamanhos, falhas, foraPorNumero), [qs, flags, tamanhos, falhas, foraPorNumero]);
  const flat = useMemo(() => tabs.reduce<Linha[]>((a, t) => a.concat(t.linhas), []), [tabs]);
  const idxCur = Math.max(0, flat.findIndex((l) => l.id === cursor));
  const cur: Linha | undefined = flat[idxCur];
  const curId = cur ? cur.id : null;

  // Ao andar pelo teclado: a linha atual rola para o meio da tela e recebe o foco
  useEffect(() => {
    if (!rolar.current || !curId) return;
    rolar.current = false;
    const el = refs.current[curId];
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const botao = el.querySelector('[data-fad06-ir]') as HTMLButtonElement | null;
      if (botao) botao.focus({ preventScroll: true });
    }
  }, [curId]);

  const nQrt = qs.length;
  if (!nQrt) {
    return (
      <div style={{ background: '#FFFFFF', border: '1px solid #D5DEEA', borderRadius: 14, padding: 24, fontSize: 15, color: COR.secundario }}>
        <div style={{ fontWeight: 700, color: COR.navy, marginBottom: 4 }}>Resumo do Reconhecimento Geográfico (FA-D-06)</div>
        Esta área não tem RG cadastrado.
      </div>
    );
  }

  const lado = (k: number) => (k % 2 === 0 ? 'Tabela da esquerda' : 'Tabela da direita');

  const ehNavegacao = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const alvo = e.target as HTMLElement;
    return alvo === e.currentTarget || !!alvo.closest('[data-fad06-nav]');
  };

  const aoTeclar = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (soProblemas || !cur || !ehNavegacao(e)) return;
    const k = e.key;
    const espaco = k === ' ' || k === 'Spacebar';
    if (!(k === 'ArrowDown' || k === 'ArrowUp' || espaco || k === 'Enter' || k === 'Backspace')) return;
    e.preventDefault();
    rolar.current = true;
    if (k === 'ArrowDown') {
      if (idxCur < flat.length - 1) setCursor(flat[idxCur + 1].id);
      return;
    }
    if (k === 'ArrowUp') {
      if (idxCur > 0) setCursor(flat[idxCur - 1].id);
      return;
    }
    if (k === 'Backspace') {
      if (idxCur > 0) {
        const anterior = flat[idxCur - 1].id;
        setCopiadas((c) => {
          const n = { ...c };
          delete n[anterior];
          return n;
        });
        setCursor(anterior);
      }
      return;
    }
    const atual = cur.id;
    setCopiadas((c) => ({ ...c, [atual]: true }));
    if (idxCur < flat.length - 1) setCursor(flat[idxCur + 1].id);
  };

  const aoSoltarTecla = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (soProblemas || !ehNavegacao(e)) return;
    if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') e.preventDefault();
  };

  const alternar = (id: string) =>
    setCopiadas((c) => {
      const n = { ...c };
      if (n[id]) delete n[id];
      else n[id] = true;
      return n;
    });

  // Mudar onde entra o subtotal: recalcula tudo depois e desmarca Transporte/Subtotal/TOTAL que mudaram de valor
  const ajustar = (k: number, novo: number | null) => {
    setTamanhos((t) => {
      const n = { ...t };
      if (novo === null) delete n[String(k)];
      else n[String(k)] = Math.max(1, novo);
      return n;
    });
    setCopiadas((c) => {
      const n: Record<string, boolean> = {};
      Object.keys(c).forEach((id) => {
        const tp = id.charAt(0);
        const num = parseInt(id.slice(1), 10);
        if ((tp === 's' || tp === 't') && num >= k) return;
        n[id] = c[id];
      });
      return n;
    });
  };

  const recomecar = () => {
    if (!window.confirm('Apagar as marcas de "copiado" desta área e ciclo e começar de novo? Os ajustes de subtotal continuam como estão.')) return;
    setCopiadas({});
    setCursor(flat.length ? flat[0].id : null);
  };

  const totalLinhas = flat.length;
  const feitas = flat.filter((l) => copiadas[l.id]).length;
  const pct = totalLinhas ? Math.round((feitas / totalLinhas) * 100) : 0;
  const problemas = qs.filter((q) => (flags.get(q.codigo) || 'ok') !== 'ok');
  const totalDesap = falhas.reduce((s, f) => s + f.qtd, 0);
  const nForaLinha = foraPorNumero.size;
  const nSoDesap = totalDesap - nForaLinha;
  const ultimaLinha = flat[flat.length - 1];
  const totalArea = ultimaLinha ? ultimaLinha.vals.total : 0;
  const rgImoveis = Number(totalImoveisRG) || 0;
  const rgQrt = Number(totalQuarteiroesRG) || 0;
  const confere = totalArea === rgImoveis && nQrt === rgQrt;

  // Lupa: onde estou, valores grandes e o que é só da tela
  let onde = '';
  let extra = '';
  const celulas: Celula[] = [];
  if (cur) {
    const folha = Math.floor(cur.tab / 2) + 1;
    const base = `Folha ${folha} · ${lado(cur.tab)}`;
    if (cur.tipo === 'desap') {
      const quantos = cur.qtd || 1;
      onde = `${base} · Linha ${cur.linhaPapel}`;
      extra =
        quantos > 1
          ? `Faixa de ${quantos} quarteirões desaparecidos: confira o RG. Não entra em nenhum total.`
          : cur.motivo
          ? 'Fora de uso no RG: no papel, o número do quarteirão e o motivo. Conta como linha, mas não entra em nenhum total.'
          : 'No papel: o número do quarteirão e a palavra "desaparecido". Conta como linha, mas não entra em nenhum total.';
      celulas.push({ rot: quantos > 1 ? 'Quarteirões' : 'Quarteirão', v: cur.rotulo, destaque: 'primeira', palavra: quantos > 1 });
      celulas.push({ rot: 'Escreva no papel', v: quantos > 1 ? `Desaparecidos (${quantos})` : cur.motivo || 'Desaparecido', destaque: 'aviso', larga: true });
    } else {
      if (cur.tipo === 'qrt' && cur.q) {
        onde = `${base} · Linha ${cur.linhaPapel}`;
        const nomes =
          cur.q.agentes && cur.q.agentes.length
            ? cur.q.agentes.map((a) => a.nome).join(', ')
            : cur.q.agente_principal || '—';
        const periodo = cur.q.data_inicio
          ? cur.q.data_inicio + (cur.q.data_fim_fad && cur.q.data_fim_fad !== cur.q.data_inicio ? ` → ${cur.q.data_fim_fad}` : '')
          : '—';
        const situacao =
          cur.flag === 'erro'
            ? 'Nunca citado em FAD-01'
            : cur.q.data_toggle
            ? `Concluído em ${cur.q.data_toggle} ✓`
            : 'Sem declaração de conclusão';
        extra = `Agente: ${nomes} · Período: ${periodo} · ${situacao}`;
      } else if (cur.tipo === 'transporte') {
        onde = `${base} · Linha 1 · Transporte`;
        extra = `Transporte = soma de tudo antes desta tabela, do Qrt ${tabs[0].primeiro} ao Qrt ${tabs[cur.tab - 1].ultimoCod}.`;
      } else {
        onde = `${base} · ${cur.rotulo}${cur.ultima ? ' (fim da área)' : ''}`;
        extra = `${cur.rotulo} = soma acumulada do Qrt ${tabs[0].primeiro} ao Qrt ${tabs[cur.tab].ultimoCod}.${cur.ultima ? ' É o total da área.' : ''}`;
      }
      const ehQ = cur.tipo === 'qrt';
      const valores = cur.vals;
      celulas.push({ rot: ehQ ? 'Quarteirão' : 'Linha do papel', v: cur.rotulo, destaque: 'primeira', palavra: !ehQ });
      TIPOS.forEach((t) => celulas.push({ rot: NOMES_TIPO[t], v: fmt(valores[t]), destaque: 'normal', zero: valores[t] === 0 }));
      celulas.push({ rot: 'Total de imóveis', v: fmt(valores.total), destaque: 'total' });
    }
  }
  const prox = flat[idxCur + 1];
  const proxTxt = !prox
    ? 'Última linha'
    : prox.tipo === 'qrt'
    ? `Próxima: Qrt ${prox.rotulo}`
    : prox.tipo === 'desap'
    ? `Próxima: ${prox.rotulo} (${prox.motivo ? 'fora de uso' : 'desaparecido'})`
    : `Próxima: ${prox.rotulo}`;

  const cabecalhoTabela = (
    <div style={{ display: 'flex', background: COR.navy }}>
      <div style={{ width: 36, flexShrink: 0 }} />
      <div
        style={{
          ...GRADE,
          flexGrow: 1,
          minHeight: 44,
          paddingRight: 10,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: '#DCE6FB',
          textAlign: 'center',
          lineHeight: 1.2,
        }}
      >
        <span />
        <span style={{ textAlign: 'left' }}>Nº do quarteirão</span>
        {TIPOS.map((t) => (
          <span key={t} title={NOMES_TIPO[t]}>{NOMES_CURTOS[t]}</span>
        ))}
        <span>Total de imóveis</span>
      </div>
    </div>
  );

  const renderLinha = (l: Linha) => {
    const ehCur = l.id === curId;
    const cop = !!copiadas[l.id];
    const qrt = l.tipo === 'qrt';
    const rod = l.tipo === 'rodape';
    const tra = l.tipo === 'transporte';
    const des = l.tipo === 'desap';
    const nLinha = l.linhaPapel || 1;
    const quantos = l.qtd || 1;
    let fundo = des
      ? '#F3F4F6'
      : qrt
      ? Math.floor((nLinha - 1) / 5) % 2 === 1
        ? '#F4F7FB'
        : '#FFFFFF'
      : tra
      ? '#F1F5F9'
      : '#E3EAF5';
    let cor = des ? COR.secundario : qrt ? COR.texto : COR.navy;
    let peso = des ? 600 : qrt ? 500 : 700;
    let fonte = 15;
    let sombra = 'none';
    let traco = COR.apagado;
    if (cop && !ehCur) {
      cor = COR.apagado;
      peso = rod ? 600 : 400;
    }
    if (ehCur) {
      fundo = '#DBEAFE';
      cor = '#0B1B36';
      peso = 700;
      fonte = 17;
      sombra = `inset 0 0 0 2px ${COR.azul}`;
      traco = '#334155';
    }
    let borda = '1px solid #EDF1F6';
    if (rod) borda = `1.5px solid ${COR.apagado}`;
    else if (tra) borda = '1px solid #D5DEEA';
    else if (nLinha > 1 && (nLinha - 1) % 5 === 0) borda = '1px solid #C9D4E3';
    let marcador: { cor: string; texto: string } | null = null;
    if (qrt && l.q) {
      if (l.flag === 'erro') marcador = { cor: COR.vermelho, texto: 'Nunca citado em FAD-01' };
      else if (!l.q.data_toggle) marcador = { cor: COR.amarelo, texto: 'Sem declaração de conclusão' };
    }
    const nome = qrt ? `quarteirão ${l.rotulo}` : des ? `${l.motivo ? 'fora de uso' : 'desaparecido'} ${l.rotulo}` : l.rotulo;
    return (
      <div
        key={l.id}
        ref={(el) => {
          refs.current[l.id] = el;
        }}
        style={{ display: 'flex', alignItems: 'stretch', background: fundo, borderTop: borda, boxShadow: sombra }}
      >
        <button
          type="button"
          data-fad06-nav="1"
          aria-label={cop ? `Desmarcar ${nome}` : `Marcar como copiada: ${nome}`}
          onClick={() => alternar(l.id)}
          style={{ width: 36, flexShrink: 0, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, outline: 'none' }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              boxSizing: 'border-box',
              borderRadius: 5,
              border: `1.5px solid ${cop ? COR.teal : '#94A3B8'}`,
              background: cop ? COR.teal : '#FFFFFF',
              color: '#FFFFFF',
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {cop ? '✓' : ''}
          </span>
        </button>
        <button
          type="button"
          data-fad06-nav="1"
          data-fad06-ir="1"
          onClick={() => setCursor(l.id)}
          style={{
            ...GRADE,
            flexGrow: 1,
            height: rod ? 38 : 32,
            border: 'none',
            background: 'transparent',
            padding: '0 10px 0 0',
            cursor: 'pointer',
            textAlign: 'center',
            color: cor,
            fontSize: fonte,
            fontWeight: peso,
            fontFamily: 'inherit',
            fontVariantNumeric: 'tabular-nums',
            outline: 'none',
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 600, color: ehCur ? COR.azul : COR.apagado, textAlign: 'right', paddingRight: 6 }}>
            {ehCur ? '▸' : qrt || des || tra ? nLinha : ''}
          </span>
          <span style={{ textAlign: 'left', fontStyle: tra ? 'italic' : 'normal', display: 'flex', alignItems: 'center', gap: 6 }}>
            {qrt || des ? (
              <span
                style={{
                  minWidth: 30,
                  padding: '0 7px',
                  borderRadius: 6,
                  lineHeight: '22px',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  background: ehCur || cop || des ? '#FFFFFF' : '#E6EDFA',
                  border: `1px ${des ? 'dashed' : 'solid'} ${ehCur ? COR.azul : des ? '#94A3B8' : cop ? '#D5DEEA' : '#B9C8E6'}`,
                  color: ehCur ? '#0B1B36' : cop ? COR.apagado : des ? COR.secundario : COR.navy,
                }}
              >
                {l.rotulo}
              </span>
            ) : (
              l.rotulo
            )}
            {marcador && (
              <span title={marcador.texto} style={{ width: 8, height: 8, borderRadius: '50%', background: marcador.cor, flexShrink: 0 }} />
            )}
          </span>
          {des ? (
            <span
              style={{
                gridColumn: '3 / -1',
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: l.motivo ? '0.02em' : '0.1em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={l.motivo || undefined}
            >
              {quantos > 1 ? `Desaparecidos · ${quantos} quarteirões` : l.motivo || 'Desaparecido'}
            </span>
          ) : (
            <>
              {TIPOS.map((t) => (
                <span key={t} style={{ color: l.vals[t] === 0 ? traco : undefined }}>
                  {fmt(l.vals[t])}
                </span>
              ))}
              <span style={{ fontWeight: 700, color: ehCur ? '#0B1B36' : cop ? COR.apagado : COR.navy }}>{fmt(l.vals.total)}</span>
            </>
          )}
        </button>
      </div>
    );
  };

  const estiloBotao = (desab: boolean): React.CSSProperties => ({
    height: 30,
    padding: '0 10px',
    borderRadius: 7,
    border: '1px solid #94A3B8',
    background: '#FFFFFF',
    color: COR.navy,
    fontSize: 12,
    fontWeight: 600,
    cursor: desab ? 'default' : 'pointer',
    opacity: desab ? 0.4 : 1,
    fontFamily: 'inherit',
  });

  const renderTabela = (t: Tabela) => {
    const cop = t.linhas.filter((l) => copiadas[l.id]).length;
    const completa = cop === t.linhas.length;
    const rod = t.k % 2 === 0 ? 'Subtotal' : 'TOTAL';
    const linhasUsadas = t.tam + (t.k > 0 ? 1 : 0);
    return (
      <div key={t.k} style={{ display: 'flex', flexDirection: 'column', border: `1px solid ${COR.borda}`, borderRadius: 10, overflow: 'hidden', background: '#FFFFFF' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#F1F5FA', borderBottom: '1px solid #D5DEEA' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COR.navy }}>{lado(t.k)}</div>
            <div style={{ fontSize: 12, color: COR.secundario }}>
              Qrt {t.primeiro} a {t.ultimoCod} · {t.reais} com imóveis
              {t.desap > 0 ? ` + ${t.desap} ${plural(t.desap, 'desaparecido', 'desaparecidos')}` : ''}
            </div>
            {linhasUsadas > LINHAS_DO_PAPEL && (
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1E40AF' }}>
                {linhasUsadas} linhas: passa das {LINHAS_DO_PAPEL} do papel
              </div>
            )}
          </div>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: completa ? COR.verdeTexto : '#334155',
              background: completa ? COR.verdeFundo : '#E2E8F0',
              padding: '3px 9px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            {completa ? '✓ ' : ''}
            {cop} de {t.linhas.length} copiadas
          </span>
        </div>
        {cabecalhoTabela}
        {t.linhas.map((l) => renderLinha(l))}
        <div
          className="no-print"
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#F8FAFC', borderTop: '1px solid #D5DEEA', fontSize: 13, color: '#334155', flexWrap: 'wrap' }}
        >
          <span style={{ flexGrow: 1 }}>
            {rod} depois de {t.tam} {plural(t.tam, 'lançamento', 'lançamentos')}{t.ajustada ? ` (padrão: ${t.padrao})` : ''}
          </span>
          {t.ajustada && (
            <span style={{ padding: '3px 9px', borderRadius: 999, background: '#DBEAFE', color: '#1E40AF', fontSize: 12, fontWeight: 600 }}>Ajustada</span>
          )}
          {t.ajustada && (
            <button type="button" onClick={() => ajustar(t.k, null)} style={{ ...estiloBotao(false), color: '#1E3A8A', border: `1px solid ${COR.borda}` }}>
              Voltar a {t.padrao}
            </button>
          )}
          <button type="button" disabled={t.tam <= 1} onClick={() => ajustar(t.k, t.tam - 1)} style={estiloBotao(t.tam <= 1)} title={`Sobe o ${rod} uma linha: esta tabela fica com 1 linha a menos`}>
            ↑ Subir
          </button>
          <button type="button" disabled={t.ultima} onClick={() => ajustar(t.k, t.tam + 1)} style={estiloBotao(t.ultima)} title={`Desce o ${rod} uma linha: esta tabela fica com 1 linha a mais`}>
            ↓ Descer
          </button>
        </div>
      </div>
    );
  };

  const folhas: { numero: number; esq: Tabela; dir?: Tabela }[] = [];
  for (let f = 0; f * 2 < tabs.length; f++) {
    folhas.push({ numero: f + 1, esq: tabs[2 * f], dir: tabs[2 * f + 1] });
  }

  return (
    <div tabIndex={0} onKeyDown={aoTeclar} onKeyUp={aoSoltarTecla} style={{ display: 'flex', flexDirection: 'column', gap: 16, outline: 'none' }}>
      <style>{`
        .fad06-folha { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; align-items: start; }
        .fad06-lupa { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 10px; }
        @media (max-width: 1220px) {
          .fad06-folha { grid-template-columns: minmax(0, 1fr); }
        }
        @media (max-width: 1000px) {
          .fad06-lupa { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        }
      `}</style>

      {/* Cabeçalho com o progresso */}
      <div style={{ background: COR.navy, borderRadius: 14, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A9BCE0' }}>
            Resumo do Reconhecimento Geográfico · FA-D-06
          </div>
          <div style={{ fontSize: 21, fontWeight: 700, color: '#FFFFFF' }}>Passar para o papel</div>
        </div>
        <div className="no-print" style={{ width: 300, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#DCE6FB' }}>
            <span style={{ fontWeight: 600 }}>
              {feitas} de {totalLinhas} linhas copiadas
            </span>
            <span>{pct}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: '#233B66', overflow: 'hidden' }}>
            <div style={{ height: 8, width: `${pct}%`, background: '#5EEAD4', borderRadius: 999 }} />
          </div>
        </div>
        <button
          type="button"
          className="no-print"
          onClick={recomecar}
          style={{ height: 40, padding: '0 16px', borderRadius: 8, border: '1px solid #4B6597', background: 'transparent', color: '#FFFFFF', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Recomeçar
        </button>
      </div>

      {/* Lupa fixa: a linha atual em números grandes */}
      {cur && !soProblemas && (
        <div
          className="no-print"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            background: '#FFFFFF',
            border: `2px solid ${COR.azul}`,
            borderRadius: 14,
            padding: '18px 22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            boxShadow: '0 8px 24px rgba(15, 35, 71, 0.12)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COR.azul }}>Você está aqui</div>
              <div style={{ fontSize: 19, fontWeight: 700, color: COR.navy }}>{onde}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13, color: COR.secundario, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={KBD}>↑</span>
                <span style={KBD}>↓</span>andar
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={KBD}>Espaço</span>copiei, desce
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={KBD}>⌫</span>desfazer
              </span>
              <span>Clique = ir para a linha</span>
            </div>
          </div>
          <div className="fad06-lupa">
            {celulas.map((c, i) => (
              <div
                key={i}
                style={{
                  gridColumn: c.larga ? '2 / -1' : undefined,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 4,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: fundoCelula(c.destaque),
                  border: bordaCelula(c.destaque),
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: corRotuloCelula(c.destaque),
                  }}
                >
                  {c.rot}
                </div>
                <div
                  style={{
                    fontSize: c.destaque === 'aviso' ? 26 : c.palavra ? 22 : 34,
                    lineHeight: '40px',
                    fontWeight: 700,
                    letterSpacing: c.destaque === 'aviso' ? '0.06em' : undefined,
                    textTransform: c.destaque === 'aviso' ? 'uppercase' : undefined,
                    fontVariantNumeric: 'tabular-nums',
                    color: corValorCelula(c),
                  }}
                >
                  {c.v}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: '#334155', flexWrap: 'wrap' }}>
            <span style={{ padding: '2px 8px', borderRadius: 6, background: '#F1F5F9', color: COR.secundario, fontSize: 12, fontWeight: 600 }}>Só na tela</span>
            <span style={{ flexGrow: 1 }}>{extra}</span>
            <span style={{ color: COR.secundario, fontWeight: 600 }}>{proxTxt}</span>
          </div>
        </div>
      )}

      {/* Barra de problemas */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 4px', flexWrap: 'wrap' }}>
        <span
          style={{
            padding: '6px 12px',
            borderRadius: 999,
            background: problemas.length ? '#FEE2E2' : COR.verdeFundo,
            color: problemas.length ? '#991B1B' : COR.verdeTexto,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {problemas.length
            ? `${problemas.length} ${problemas.length === 1 ? 'quarteirão' : 'quarteirões'} com problema`
            : '✓ Nenhum quarteirão com problema'}
        </span>
        <button
          type="button"
          onClick={() => setSoProblemas((s) => !s)}
          style={{
            height: 34,
            padding: '0 14px',
            borderRadius: 8,
            border: `1px solid ${soProblemas ? COR.navy : COR.borda}`,
            background: soProblemas ? COR.navy : '#FFFFFF',
            color: soProblemas ? '#FFFFFF' : COR.navy,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {soProblemas ? 'Mostrando só problemas' : 'Só problemas'}
        </button>
        <span style={{ flexGrow: 1 }} />
        <span style={{ fontSize: 13, color: COR.secundario }}>Agente e período ficam na lupa: não vão para o papel.</span>
      </div>

      {/* Desaparecidos: falhas na numeração, para conferir antes de copiar */}
      {!soProblemas && (falhas.length > 0 || foraDeUso.length > 0) && (
        <div
          className="no-print"
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12, background: '#F1F5F9', border: `1px solid ${COR.borda}`, fontSize: 13, color: '#334155', flexWrap: 'wrap' }}
        >
          <span title="Falha na numeração dos quarteirões do RG" style={{ fontWeight: 700, color: COR.navy }}>
            {nSoDesap} {plural(nSoDesap, 'desaparecido', 'desaparecidos')}
            {nForaLinha > 0 ? ` + ${nForaLinha} fora de uso (com motivo)` : ''}
          </span>
          <span>fora dos totais:</span>
          {falhas.map((f) => (
            <span
              key={f.de}
              style={{ padding: '2px 9px', borderRadius: 6, background: '#FFFFFF', border: '1px dashed #94A3B8', fontWeight: 600, color: COR.secundario, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
            >
              {f.de === f.ate ? f.de : `${f.de} a ${f.ate}`}
            </span>
          ))}
          <span style={{ flexGrow: 1 }} />
          <span>Cada um gasta uma linha do papel.</span>
          {nForaLinha > 0 && (
            <span style={{ flexBasis: '100%' }}>
              Fora de uso (com motivo): {Array.from(foraPorNumero.entries()).map(([n, fu]) => `${n} — ${fu.motivo || 'sem motivo'}`).join(' · ')}
            </span>
          )}
          {foraSemLinha.length > 0 && (
            <span style={{ flexBasis: '100%' }}>
              Fora de uso sem linha própria: {foraSemLinha.map((fu) => `${fu.codigo} — ${fu.motivo || 'sem motivo'}`).join(' · ')}
            </span>
          )}
        </div>
      )}

      {/* Só problemas: sem Transporte, Subtotal nem TOTAL */}
      {soProblemas && (
        <div style={{ background: '#FFFFFF', border: '1px solid #D5DEEA', borderRadius: 14, padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: COR.navy }}>Só problemas</div>
          <div style={{ fontSize: 14, color: COR.secundario }}>
            Enquanto este filtro está ligado, Transporte, Subtotal e TOTAL ficam escondidos: eles só valem com todas as linhas na tela.
          </div>
          {problemas.length === 0 ? (
            <div style={{ fontSize: 15, color: COR.verdeTexto, fontWeight: 600 }}>Nenhum quarteirão com problema nesta área e ciclo.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', border: `1px solid ${COR.borda}`, borderRadius: 10, overflow: 'hidden' }}>
              {problemas.map((q, i) => {
                const erro = (flags.get(q.codigo) || 'ok') === 'erro';
                return (
                  <div
                    key={`${q.codigo}-${i}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px', borderTop: i ? '1px solid #EDF1F6' : 'none', fontVariantNumeric: 'tabular-nums', flexWrap: 'wrap' }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: erro ? COR.vermelho : COR.amarelo, flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, color: COR.navy, minWidth: 80 }}>Qrt {q.codigo}</span>
                    <span style={{ color: erro ? '#991B1B' : '#854D0E', fontWeight: 600, flexGrow: 1 }}>
                      {erro ? 'Nunca citado em FAD-01' : 'Citado, mas sem declaração de conclusão'}
                    </span>
                    <span style={{ color: COR.secundario }}>
                      R {fmt(q.R)} · C {fmt(q.C)} · TB {fmt(q.TB)} · PE {fmt(q.PE)} · O {fmt(q.O)} · Total {fmt(q.total)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div>
            <button
              type="button"
              onClick={() => setSoProblemas(false)}
              style={{ height: 36, padding: '0 16px', borderRadius: 8, border: `1px solid ${COR.navy}`, background: COR.navy, color: '#FFFFFF', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Voltar para todas as linhas
            </button>
          </div>
        </div>
      )}

      {/* As folhas, iguais ao papel: 2 tabelas lado a lado */}
      {!soProblemas &&
        folhas.map((f) => {
          const usadas = f.dir ? [f.esq, f.dir] : [f.esq];
          const tot = usadas.reduce((s, t) => s + t.linhas.length, 0);
          const fe = usadas.reduce((s, t) => s + t.linhas.filter((l) => copiadas[l.id]).length, 0);
          const nq = usadas.reduce((s, t) => s + t.reais, 0);
          const nd = usadas.reduce((s, t) => s + t.desap, 0);
          return (
            <div
              key={f.numero}
              style={{ background: '#FFFFFF', border: '1px solid #D5DEEA', borderRadius: 14, padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: COR.navy }}>Folha {f.numero}</div>
                <div style={{ fontSize: 14, color: COR.secundario }}>
                  {nq} com imóveis
                  {nd > 0 ? ` + ${nd} ${plural(nd, 'desaparecido', 'desaparecidos')}` : ''} · {fe} de {tot} linhas copiadas
                </div>
                <span style={{ flexGrow: 1 }} />
                {fe === tot && (
                  <span style={{ padding: '4px 12px', borderRadius: 999, background: COR.verdeFundo, color: COR.verdeTexto, fontSize: 13, fontWeight: 600 }}>
                    Folha copiada ✓
                  </span>
                )}
              </div>
              <div className="fad06-folha">
                {renderTabela(f.esq)}
                {f.dir ? (
                  renderTabela(f.dir)
                ) : (
                  <div
                    style={{
                      minHeight: 240,
                      border: `1.5px dashed ${COR.borda}`,
                      borderRadius: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      padding: 24,
                      textAlign: 'center',
                      color: COR.secundario,
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#334155' }}>Tabela da direita — não usada</div>
                    <div style={{ fontSize: 13 }}>A área termina na tabela da esquerda desta folha.</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

      {/* Total final conferido com o RG */}
      {!soProblemas && (
        <div style={{ background: COR.navy, borderRadius: 14, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A9BCE0' }}>Total da área no papel</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#FFFFFF', fontVariantNumeric: 'tabular-nums' }}>
              {totalArea.toLocaleString('pt-BR')} imóveis · {nQrt} quarteirões
            </div>
            {totalDesap > 0 && (
              <div style={{ fontSize: 13, color: '#DCE6FB' }}>
                + {nSoDesap} {plural(nSoDesap, 'desaparecido', 'desaparecidos')}
                {nForaLinha > 0 ? ` e ${nForaLinha} fora de uso` : ''}, fora de qualquer total
              </div>
            )}
          </div>
          <span
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              background: confere ? COR.verdeFundo : '#FEE2E2',
              color: confere ? COR.verdeTexto : '#991B1B',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {confere ? '✓ Confere com o RG: ' : 'Não confere com o RG: '}
            {rgImoveis.toLocaleString('pt-BR')} imóveis · {rgQrt} quarteirões
          </span>
        </div>
      )}
    </div>
  );
}
